import {
    faArrowRightToBracket,
    faChevronDown,
    faChevronsLeft,
    faChevronsRight,
    faChevronUp,
    faCircleQuestion,
    faGearComplex,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePostHog } from '@posthog/react';
import Action from '@sharedComponents/Action';
import Button from '@sharedComponents/Button';
import Divider from '@sharedComponents/Divider';
import Loader from '@sharedComponents/Loader';
import Tooltip from '@sharedComponents/Tooltip';
import Typography from '@sharedComponents/Typography';
import { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';

import * as style from './style.module.css';

const getActionProps = (item, posthog) => {
    if (typeof item.action === 'function') {
        return {
            action: () => {
                if (item.posthogEvent) posthog?.capture(item.posthogEvent);
                item.action();
            },
        };
    }
    return {
        action: item.action,
        onClick: item.posthogEvent ? () => posthog?.capture(item.posthogEvent) : undefined,
    };
};

const Menu = ({
    t,
    logo = null,
    loading = false,
    navItems = [],
    headerExtra = null,
    collapsedHeaderExtra = null,
    footer = {},
    isOpen = false,
    onToggle = () => {},
}) => {
    const posthog = usePostHog();
    const [openSections, setOpenSections] = useState({});

    const toggleSection = (id) => {
        setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const openMenu = () => onToggle(!isOpen);

    useEffect(() => {
        const activeGroup = navItems.find((item) => item.subItems?.some((sub) => sub.active));
        if (activeGroup) {
            setOpenSections((prev) => ({ ...prev, [activeGroup.id]: true }));
        }
    }, [navItems]);

    const renderNavItem = (item) => {
        if (item.subItems) {
            const isExpanded = openSections[item.id];
            const handleSectionClick = () => {
                if (item.posthogEvent) posthog?.capture(item.posthogEvent);
                toggleSection(item.id);
            };
            return (
                <div key={item.id} className={style.navGroup}>
                    <div
                        className={`${style.navGroupHeader} ${item.active ? style.active : ''}`}
                        onClick={handleSectionClick}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSectionClick()}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                    >
                        <span className={style.navIconWrap}>
                            <FontAwesomeIcon icon={item.icon} className={style.navIcon} />
                        </span>
                        <Typography div className={style.navLabel} fontSize="s">
                            {item.label}
                        </Typography>
                        <span className={style.navChevronWrap}>
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className={style.navChevron} />
                        </span>
                    </div>
                    {isExpanded && (
                        <div className={style.navSubItems}>
                            {item.subItems.map((sub) => (
                                <Action
                                    key={sub.id}
                                    {...getActionProps(sub, posthog)}
                                    className={`${style.navSubItem} ${sub.active ? style.active : ''}`}
                                >
                                    {sub.label}
                                </Action>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <Action key={item.id} {...getActionProps(item, posthog)} className={`${style.navItem} ${item.active ? style.active : ''}`}>
                <span className={style.navIconWrap}>
                    <FontAwesomeIcon icon={item.icon} className={style.navIcon} />
                </span>
                <Typography div className={style.navLabel} fontSize="s">
                    {item.label}
                </Typography>
            </Action>
        );
    };

    return (
        <>
            <div className={`${style.collapsedSidebar} ${isOpen ? style.collapsedHidden : ''}`} aria-hidden={isOpen}>
                <div className={style.collapsedContent}>
                    <div className={style.collapsedNav}>
                        <div className={style.collapsedHeader}>
                            <div
                                className={style.collapsedOpenBtn}
                                onClick={openMenu}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openMenu()}
                                role="button"
                                tabIndex={0}
                                aria-label={t('Open menu')}
                            >
                                <FontAwesomeIcon icon={faChevronsRight} />
                            </div>
                            {collapsedHeaderExtra}
                        </div>
                        <div className={style.collapsedNavItems}>
                            {navItems.map((item) => (
                                <Tooltip key={item.id} text={item.label} position="right" popupIcon={false}>
                                    <Action
                                        action={item.subItems ? item.subItems[0].action : item.action}
                                        className={`${style.collapsedNavItem} ${item.active ? style.active : ''}`}
                                        aria-label={item.label}
                                    >
                                        <FontAwesomeIcon icon={item.icon} className={style.navIcon} />
                                    </Action>
                                </Tooltip>
                            ))}
                        </div>
                    </div>
                    <div className={style.collapsedFooter}>
                        <Tooltip text={t('Help')} position="right" popupIcon={false}>
                            <Action
                                action={footer.helpAction}
                                onClick={() => posthog?.capture('menu:help_click')}
                                className={style.collapsedNavItem}
                                aria-label={t('Help')}
                            >
                                <FontAwesomeIcon icon={faCircleQuestion} className={style.navIcon} />
                            </Action>
                        </Tooltip>
                        <Divider />
                        <Tooltip text={t('Parameters')} position="right" popupIcon={false}>
                            <Action
                                action={footer.settingsAction}
                                onClick={() => posthog?.capture('menu:settings_click')}
                                className={`${style.collapsedNavItem} ${footer.settingsActive ? style.active : ''}`}
                                aria-label={t('Parameters')}
                            >
                                <FontAwesomeIcon icon={faGearComplex} className={style.navIcon} />
                            </Action>
                        </Tooltip>
                        <Tooltip text={t('Disconnect')} position="right" popupIcon={false}>
                            <Action action={footer.disconnectAction} className={style.collapsedNavItem} aria-label={t('Disconnect')}>
                                <FontAwesomeIcon icon={faArrowRightToBracket} className={style.navIcon} />
                            </Action>
                        </Tooltip>
                    </div>
                </div>
            </div>
            <div className={`${style.container} ${!isOpen ? style.containerHidden : ''}`}>
                <div className={style.content}>
                    <div className={style.nav}>
                        <div className={style.header}>
                            <div className={style.logoAndClose}>
                                <div className={style.logo}>
                                    <img
                                        src={logo}
                                        alt="logo"
                                        style={{ maxHeight: '32px', maxWidth: '100%', width: 'auto', height: 'auto' }}
                                    />
                                </div>
                                <div
                                    className={style.collapseBtn}
                                    onClick={openMenu}
                                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openMenu()}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={t('Close menu')}
                                >
                                    <FontAwesomeIcon icon={faChevronsLeft} />
                                </div>
                            </div>
                            {headerExtra}
                        </div>
                        <div className={style.navigation}>
                            {loading && <Loader />}
                            {!loading && navItems.map(renderNavItem)}
                        </div>
                    </div>
                    <div className={style.footer}>
                        <Action action={footer.helpAction} onClick={() => posthog?.capture('menu:help_click')} className={style.navItem}>
                            <span className={style.navIconWrap}>
                                <FontAwesomeIcon icon={faCircleQuestion} className={style.navIcon} />
                            </span>
                            <Typography div className={style.navLabel} fontSize="s">
                                {t('Help')}
                            </Typography>
                        </Action>
                        <Divider />
                        <Action
                            action={footer.settingsAction}
                            onClick={() => posthog?.capture('menu:settings_click')}
                            className={`${style.navItem} ${footer.settingsActive ? style.active : ''}`}
                        >
                            <span className={style.navIconWrap}>
                                <FontAwesomeIcon icon={faGearComplex} className={style.navIcon} />
                            </span>
                            <Typography div className={style.navLabel} fontSize="s">
                                {t('Parameters')}
                            </Typography>
                        </Action>
                        <div className={style.account}>
                            <div className={style.accountInfo}>
                                <Typography color="neutral-primary" fontWeight="regular" fontSize="xs">
                                    {footer.user?.firstName}
                                </Typography>
                                <Typography color="neutral-tertiary" fontWeight="regular" fontSize="xs">
                                    {footer.user?.roleLabel}
                                </Typography>
                            </div>
                            <Button action={footer.disconnectAction} icon={faArrowRightToBracket} ghost />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default withTranslation()(Menu);
